import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { d as defineCustomElement$i } from './box.js';
import { d as defineCustomElement$h } from './button.js';
import { d as defineCustomElement$g } from './check-input.js';
import { d as defineCustomElement$f } from './close-button.js';
import { d as defineCustomElement$e } from './component-header.js';
import { d as defineCustomElement$d } from './empty-state.js';
import { d as defineCustomElement$c } from './form-field.js';
import { d as defineCustomElement$b } from './graph-icon.js';
import { d as defineCustomElement$a } from './icon.js';
import { d as defineCustomElement$9 } from './layout.js';
import { d as defineCustomElement$8 } from './loader.js';
import { d as defineCustomElement$7 } from './logo.js';
import { d as defineCustomElement$6 } from './status.js';
import { d as defineCustomElement$5 } from './tag.js';
import { d as defineCustomElement$4 } from './thumbnail.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const thumbnailGridCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.content{display:flex;flex-wrap:wrap;gap:1rem;height:100%;overflow-y:auto;width:100%}.content .header{position:sticky;top:0;width:100%;z-index:7}.content .header .header-wrapper{align-content:center;display:flex;justify-content:space-between;min-height:2rem;width:100%}.state-empty,.state-error,.state-loading{align-items:center;background-color:var(--jw-theme-surface-level1-default-bg-color);display:flex;font-family:Open Sans;font-size:.875rem;font-weight:400;justify-content:center;letter-spacing:-.0004em;line-height:1.25rem;min-height:15rem}.state-error{color:var(--jw-theme-form-validation-error-color);gap:.25rem}:host([scroll-size=sm][size=sm]) .content{max-height:7rem}:host([scroll-size=sm][size=md]) .content{max-height:10rem}:host([scroll-size=sm][size=lg]) .content{max-height:19rem}:host([scroll-size=sm][size=xl]) .content{max-height:21rem}:host([scroll-size=md][size=sm]) .content{max-height:11rem}:host([scroll-size=md][size=md]) .content{max-height:15.5rem}:host([scroll-size=md][size=lg]) .content{max-height:29rem}:host([scroll-size=md][size=xl]) .content{max-height:32rem}:host([scroll-size=lg][size=sm]) .content{max-height:15rem}:host([scroll-size=lg][size=md]) .content{max-height:21rem}:host([scroll-size=lg][size=lg]) .content{max-height:39rem}:host([scroll-size=lg][size=xl]) .content{max-height:43rem}";
const WuiThumbnailGridStyle0 = thumbnailGridCss;

const defaultEmptyState = {
    icon: 'empty',
    heading: 'No images have been added',
    description: 'Added images will appear here.',
};
const WuiThumbnailGrid$1 = /*@__PURE__*/ proxyCustomElement(class WuiThumbnailGrid extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.delete = createEvent(this, "delete", 3);
        this.edit = createEvent(this, "edit", 3);
        this.handleCheckAllInput = (e) => {
            e.stopPropagation();
            if (this.areAllOptionsChecked()) {
                this.setValue([]);
            }
            else {
                this.setValue(this.thumbnails?.map((el) => el.id));
            }
        };
        this.handleInput = (e, id) => {
            e.stopPropagation();
            const target = e.target;
            const { checked } = target;
            if (this.disabled) {
                e.preventDefault();
                return;
            }
            if (checked) {
                this.setValue([...(this.value || []), id]);
            }
            else {
                this.setValue(this.value?.filter((id) => id !== id));
            }
        };
        this.handleEdit = (evt, id) => {
            evt.stopPropagation();
            if (this.disabled || !id) {
                evt.preventDefault();
                return;
            }
            this.edit.emit(id);
        };
        this.handleDelete = (evt, id) => {
            evt.stopPropagation();
            if (this.disabled || !id) {
                evt.preventDefault();
                return;
            }
            this.delete.emit([id]);
        };
        this.handleBulkDelete = (e) => {
            e.stopPropagation();
            this.delete.emit(this.value);
        };
        this.isChecked = (id) => {
            return Boolean(this.value?.includes(id));
        };
        this.areSomeOptionsChecked = () => {
            return (typeof this.thumbnails !== 'undefined' &&
                typeof this.value !== 'undefined' &&
                this.value.length > 0 &&
                this.value.length !== this.thumbnails.length);
        };
        this.areAllOptionsChecked = () => {
            return (typeof this.thumbnails !== 'undefined' &&
                typeof this.value !== 'undefined' &&
                this.value.length === this.thumbnails.length);
        };
        this.isEmpty = () => {
            return Boolean(!this.error && !this.loading && !this.thumbnails?.length);
        };
        this.showThumbnails = () => {
            return Boolean(!this.error && !this.loading && this.thumbnails?.length);
        };
        this.thumbnails = [];
        this.bulkSelect = false;
        this.size = 'lg';
        this.blurImage = true;
        this.error = undefined;
        this.value = undefined;
        this.loading = undefined;
        this.disabled = undefined;
        this.scrollSize = undefined;
        this.emptyStateContent = undefined;
    }
    setValue(value) {
        this.value = value;
        this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
    }
    render() {
        return (h(Host, { key: '9ac11955c9f0831151c0ca338b0826a7465b2368' }, this.loading && (h("div", { key: '4f17c681121d57b21b458091b19493f12099a2d6', class: "state-loading" }, h("wui-loader", { key: 'ce6fa0b15b4619a3f74ace321cd5a5ac7d42a927', size: "lg" }))), !this.loading && this.error && (h("div", { key: 'fc8923b00177a50d9d35e10a96dc30456981ed9e', class: "state-error" }, h("wui-icon", { key: 'ac845ceb3b02f227ceb9fdbde7913dc36ec71663', name: "alert-circle", size: "xl" }), " ", this.error.message)), !this.loading && !this.error && this.isEmpty() && (h("wui-empty-state", { key: '5949ecc535fdbf950536cf0cf88fc6d2a71049a6', contents: this.emptyStateContent ? this.emptyStateContent : defaultEmptyState })), this.showThumbnails() && (h("div", { key: '396d2166573980b1e887386e4fca170702bd3e43', class: "content" }, this.bulkSelect && (h("wui-component-header", { key: '0b2a2e81dc2fd7395eab67eccc1a5d173fc11109', class: "header" }, h("div", { key: '03b0fa19276acdc6e623bab24886b24e3b9f88eb', class: "header-wrapper" }, h("wui-check-input", { key: '4b1ef7816c276633d8e3b182ac93785316cdc94e', checked: this.areAllOptionsChecked(), disabled: this.disabled, onInput: this.handleCheckAllInput, label: this.areAllOptionsChecked() ? `${this.value?.length || 0} selected` : 'Select all', indeterminate: this.areSomeOptionsChecked() }), (this.areSomeOptionsChecked() || this.areAllOptionsChecked()) && (h("wui-button", { key: '2eca157adf189dd79d97fe464cdd224d846a4f65', label: "Delete", intent: "error", icon: "trash", type: "tertiary", onClick: this.handleBulkDelete }))))), this.thumbnails?.map(({ id, label, badge, src, ...rest }) => (h("wui-thumbnail", { key: id, label: label, badge: badge, src: src, blurImage: this.blurImage, size: this.size, checked: this.isChecked(id), checkable: !this.disabled && this.bulkSelect, disabled: this.disabled, onInput: (e) => this.handleInput(e, id), hoverActions: [
                {
                    type: 'tertiary',
                    label: 'Edit',
                    icon: 'pencil',
                    onClick: (e) => this.handleEdit(e, id),
                },
                {
                    type: 'tertiary',
                    label: 'Delete',
                    icon: 'trash',
                    onClick: (e) => this.handleDelete(e, id),
                },
            ], ...rest })))))));
    }
    get el() { return this; }
    static get style() { return WuiThumbnailGridStyle0; }
}, [1, "wui-thumbnail-grid", {
        "thumbnails": [16],
        "bulkSelect": [4, "bulk-select"],
        "size": [513],
        "blurImage": [4, "blur-image"],
        "error": [16],
        "value": [1040],
        "loading": [4],
        "disabled": [4],
        "scrollSize": [513, "scroll-size"],
        "emptyStateContent": [16]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-thumbnail-grid", "wui-box", "wui-button", "wui-check-input", "wui-close-button", "wui-component-header", "wui-empty-state", "wui-form-field", "wui-graph-icon", "wui-icon", "wui-layout", "wui-loader", "wui-logo", "wui-status", "wui-tag", "wui-thumbnail", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-thumbnail-grid":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiThumbnailGrid$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-component-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-empty-state":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-graph-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-logo":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-tag":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-thumbnail":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiThumbnailGrid = WuiThumbnailGrid$1;
const defineCustomElement = defineCustomElement$1;

export { WuiThumbnailGrid, defineCustomElement };

//# sourceMappingURL=wui-thumbnail-grid.js.map